import { forwardRef, type ReactElement, type ReactNode } from 'react';
import { Button as RadixButton, Spinner } from '@radix-ui/themes';
import { applySizeClass, cn } from '@/lib/utils';
import { Box } from './Box';
import { ButtonSize, sizeMapper } from './RadixProps';

/* Documentation for RadixTheme Button
https://www.radix-ui.com/themes/docs/components/button
*/
interface ButtonProps extends Omit<React.ComponentProps<typeof RadixButton>, 'size'> {
  size?: ButtonSize;
  fullWidth?: boolean;
  isLoading?: boolean;
  icon?: ReactElement;
  prefixIcon?: ReactElement;
  suffixIcon?: ReactElement;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      size = 'md',
      fullWidth = false,
      isLoading = false,
      icon,
      prefixIcon,
      suffixIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    let content: ReactNode;
    if (isLoading) {
      content = <Spinner size="1" />;
    } else if (icon) {
      content = <span className="shrink-0">{applySizeClass(icon, size)}</span>;
    } else {
      content = (
        <>
          {prefixIcon && <span className="shrink-0">{applySizeClass(prefixIcon, size)}</span>}
          {children}
          {suffixIcon && <span className="shrink-0">{applySizeClass(suffixIcon, size)}</span>}
        </>
      );
    }

    return (
      <RadixButton
        ref={ref}
        disabled={disabled || isLoading}
        size={sizeMapper[size]}
        className={cn((fullWidth || size === 'lg') && 'w-full!', className)}
        {...props}
      >
        {content}
      </RadixButton>
    );
  }
);

Button.displayName = 'Button';

interface FloatingButtonProps extends ButtonProps {
  containerClassName?: string;
}

const FloatingButton = forwardRef<HTMLButtonElement, FloatingButtonProps>(
  (
    {
      className,
      containerClassName,
      color = 'gray',
      highContrast = true,
      size = 'lg',
      fullWidth = true,
      ...props
    },
    ref
  ) => (
    <Box
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      className={cn('pb-safe-area z-10', containerClassName)}
    >
      <Button
        ref={ref}
        className={className}
        color={color}
        highContrast={highContrast}
        size={size}
        fullWidth={fullWidth}
        {...props}
      />
    </Box>
  )
);

FloatingButton.displayName = 'FloatingButton';

export { Button, FloatingButton };
export type { ButtonProps, ButtonSize, FloatingButtonProps };
